import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardBody } from '../../../components/card/card.jsx';
import ToolSelector from '../../small-pentest/components/ToolSelector.jsx';
import { useScanners } from '../../small-pentest/hooks/useScanners.js';
import { useCronExpression } from '../../../hooks/useCronTasks.js';
import toastService from '../../../services/toastService.js';

/**
 * Form component for creating/editing cron tasks
 */
const CronTaskForm = ({ 
  task = null, 
  onSubmit, 
  onCancel, 
  isLoading = false,
  userRole = 'user'
}) => {
  const pentestServer = process.env.REACT_APP_SMALL_PENTEST_SERVER;
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    targetUrl: '',
    isEnabled: true,
    scanOptions: {
      timeout: 300,
      depth: 2
    }
  });
  
  const [formErrors, setFormErrors] = useState({});
  
  // Cron expression management
  const {
    expression,
    setExpression,
    isValid: isCronValid,
    description: cronDescription,
    presets: cronPresets,
    setPreset
  } = useCronExpression(task?.cronExpression || '0 9 * * *');
  
  // Tool selection management
  const {
    scanners,
    selectedTools,
    isLoadingScanners,
    scannersError,
    scannersByCategory,
    availablePresets,
    hasToolPermission,
    handleToolSelection,
    handleSelectAll,
    handleClearAll,
    applyPreset,
    setSelectedToolsManually,
    fetchScanners
  } = useScanners(pentestServer, userRole);
  
  // Override the default scanner loading behavior to prevent auto-selection
  useEffect(() => {
    if (pentestServer && userRole) {
      // Don't set defaults when in form context to allow manual tool selection
      fetchScanners(false);
    }
  }, [pentestServer, userRole, fetchScanners]);
  
  // Initialize form data when editing
  useEffect(() => {
    if (task) {
      setFormData({
        name: task.name || '',
        description: task.description || '',
        targetUrl: task.targetUrl || '',
        isEnabled: task.isEnabled !== undefined ? task.isEnabled : true,
        scanOptions: {
          timeout: task.scanOptions?.timeout || 300,
          depth: task.scanOptions?.depth || 2
        }
      });
      setExpression(task.cronExpression || '0 9 * * *');
      
      // Set selected tools if editing
      if (task.selectedTools && Array.isArray(task.selectedTools)) {
        // The useScanners hook will handle setting selectedTools
        // We need to update it after scanners are loaded
      }
    }
  }, [task, setExpression]);
  
  // Update selected tools when editing and scanners are loaded
  useEffect(() => {
    if (task && task.selectedTools && scanners.length > 0) {
      // Validate that the tools still exist and user has permission
      const validTools = task.selectedTools.filter(toolId => {
        const scanner = scanners.find(s => s.id === toolId);
        return scanner && hasToolPermission(scanner.requiredRole || 'user');
      });
      
      // Set the valid tools directly
      setSelectedToolsManually(validTools);
      
      if (validTools.length !== task.selectedTools.length) {
        const removedTools = task.selectedTools.filter(toolId => !validTools.includes(toolId));
        if (removedTools.length > 0) {
          toastService.warning(`Some tools were removed due to permission changes: ${removedTools.join(', ')}`);
        }
      }
    }
  }, [task, scanners, hasToolPermission, setSelectedToolsManually]);
  
  // Handle form field changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };
  
  // Handle scan options changes
  const handleScanOptionChange = (option, value) => {
    setFormData(prev => ({
      ...prev,
      scanOptions: {
        ...prev.scanOptions,
        [option]: value
      }
    }));
  };
  
  // Validate form
  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Task name is required';
    }
    
    if (!formData.targetUrl.trim()) {
      errors.targetUrl = 'Target URL is required';
    } else {
      // Basic URL validation
      try {
        new URL(formData.targetUrl);
      } catch {
        errors.targetUrl = 'Please enter a valid URL';
      }
    }
    
    if (!isCronValid) {
      errors.cronExpression = 'Please enter a valid cron expression';
    }
    
    if (selectedTools.length === 0) {
      errors.tools = 'Please select at least one security tool';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toastService.error('Please fix the form errors before submitting');
      return;
    }
    
    const taskData = {
      ...formData,
      cronExpression: expression,
      selectedTools: selectedTools
    };
    
    try {
      await onSubmit(taskData);
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };
  
  return (
    <div className="cron-task-form">
      <Card>
        <CardHeader>
          <h4 className="mb-0">
            <i className="bi bi-plus-circle me-2"></i>
            {task ? 'Edit Cron Task' : 'Create New Cron Task'}
          </h4>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleSubmit}>
            {/* Basic Information */}
            <div className="row mb-4">
              <div className="col-md-6">
                <label className="form-label">Task Name *</label>
                <input
                  type="text"
                  className={`form-control ${formErrors.name ? 'is-invalid' : ''}`}
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Enter task name"
                  disabled={isLoading}
                />
                {formErrors.name && (
                  <div className="invalid-feedback">{formErrors.name}</div>
                )}
              </div>
              
              <div className="col-md-6">
                <label className="form-label">Target URL *</label>
                <input
                  type="url"
                  className={`form-control ${formErrors.targetUrl ? 'is-invalid' : ''}`}
                  value={formData.targetUrl}
                  onChange={(e) => handleInputChange('targetUrl', e.target.value)}
                  placeholder="https://example.com"
                  disabled={isLoading}
                />
                {formErrors.targetUrl && (
                  <div className="invalid-feedback">{formErrors.targetUrl}</div>
                )}
              </div>
            </div>
            
            <div className="row mb-4">
              <div className="col-12">
                <label className="form-label">Description</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Optional description for this cron task"
                  disabled={isLoading}
                />
              </div>
            </div>
            
            {/* Schedule Configuration */}
            <div className="mb-4">
              <Card>
                <CardHeader>
                  <h5 className="mb-0">
                    <i className="bi bi-clock me-2"></i>
                    Schedule Configuration
                  </h5>
                </CardHeader>
                <CardBody>
                  {/* Cron Expression Presets */}
                  <div className="mb-3">
                    <label className="form-label">Quick Presets</label>
                    <div className="d-flex flex-wrap gap-2">
                      {Object.entries(cronPresets).map(([label, expr]) => (
                        <button
                          key={label}
                          type="button"
                          className={`btn btn-sm ${
                            expression === expr ? 'btn-primary' : 'btn-outline-primary'
                          }`}
                          onClick={() => setPreset(expr)}
                          disabled={isLoading}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Cron Expression Input */}
                  <div className="mb-3">
                    <label className="form-label">Cron Expression *</label>
                    <input
                      type="text"
                      className={`form-control font-monospace ${
                        formErrors.cronExpression ? 'is-invalid' : 
                        isCronValid ? 'is-valid' : ''
                      }`}
                      value={expression}
                      onChange={(e) => setExpression(e.target.value)}
                      placeholder="0 9 * * * (minute hour day month dayOfWeek)"
                      disabled={isLoading}
                    />
                    {formErrors.cronExpression && (
                      <div className="invalid-feedback">{formErrors.cronExpression}</div>
                    )}
                    <div className="form-text">
                      <strong>Schedule:</strong> {cronDescription}
                    </div>
                    <div className="form-text text-muted">
                      Format: minute (0-59) hour (0-23) day (1-31) month (1-12) dayOfWeek (0-6)
                    </div>
                  </div>
                </CardBody>
              </Card>
            </div>
            
            {/* Tool Selection */}
            <div className="mb-4">
              {scannersError && (
                <div className="alert alert-danger">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  Failed to load available tools: {scannersError}
                </div>
              )}
              
              {formErrors.tools && (
                <div className="alert alert-danger">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {formErrors.tools}
                </div>
              )}
              
              <ToolSelector
                scanners={scanners}
                selectedTools={selectedTools}
                scannersByCategory={scannersByCategory}
                availablePresets={availablePresets}
                userRole={userRole}
                hasToolPermission={hasToolPermission}
                onToolSelection={handleToolSelection}
                onSelectAll={handleSelectAll}
                onClearAll={handleClearAll}
                onApplyPreset={applyPreset}
                isLoading={isLoading || isLoadingScanners}
              />
            </div>
            
            {/* Advanced Options */}
            <div className="mb-4">
              <Card>
                <CardHeader>
                  <h5 className="mb-0">
                    <i className="bi bi-gear me-2"></i>
                    Advanced Options
                  </h5>
                </CardHeader>
                <CardBody>
                  <div className="row">
                    <div className="col-md-6">
                      <label className="form-label">Timeout (seconds)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={formData.scanOptions.timeout}
                        onChange={(e) => handleScanOptionChange('timeout', parseInt(e.target.value) || 300)}
                        min="60"
                        max="3600"
                        disabled={isLoading}
                      />
                      <div className="form-text">Maximum time to wait for scan completion</div>
                    </div>
                    
                    <div className="col-md-6">
                      <label className="form-label">Scan Depth</label>
                      <select
                        className="form-select"
                        value={formData.scanOptions.depth}
                        onChange={(e) => handleScanOptionChange('depth', parseInt(e.target.value))}
                        disabled={isLoading}
                      >
                        <option value={1}>Light (1)</option>
                        <option value={2}>Normal (2)</option>
                        <option value={3}>Deep (3)</option>
                      </select>
                      <div className="form-text">Scanning intensity level</div>
                    </div>
                  </div>
                  
                  <div className="row mt-3">
                    <div className="col-12">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="enabledCheck"
                          checked={formData.isEnabled}
                          onChange={(e) => handleInputChange('isEnabled', e.target.checked)}
                          disabled={isLoading}
                        />
                        <label className="form-check-label" htmlFor="enabledCheck">
                          Enable this cron task immediately
                        </label>
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </div>
            
            {/* Form Actions */}
            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onCancel}
                disabled={isLoading}
              >
                <i className="bi bi-x-circle me-2"></i>
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isLoading || !isCronValid || selectedTools.length === 0}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    {task ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle me-2"></i>
                    {task ? 'Update Task' : 'Create Task'}
                  </>
                )}
              </button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
};

export default CronTaskForm;